// ============================================
// Audit Log Redaction Utility
//
// Strips sensitive fields from audit log entries so
// an auditor can review security events without
// exposure to PII, PHI, IP addresses, or actor identity.
// ============================================

import type { AuditLogEntry } from '../types/dataops';

/** Fields within `details` that may contain sensitive data */
const SENSITIVE_DETAIL_KEYS = new Set([
  'email', 'userEmail', 'user_email', 'patientEmail', 'patient_email',
  'name', 'userName', 'user_name', 'patientName', 'patient_name',
  'phone', 'phoneNumber', 'phone_number',
  'ssn', 'dob', 'dateOfBirth', 'date_of_birth',
  'address', 'streetAddress', 'street_address',
  'ipAddress', 'ip_address', 'ip',
  'sessionId', 'session_id', 'userId', 'user_id', 'patientId', 'patient_id',
  'token', 'apiKey', 'api_key', 'secret', 'password',
  'rawText', 'inputText', 'originalContent', 'sanitizedContent',
  'labValues', 'lab_values', 'biomarkerData', 'biomarker_data',
  'medications', 'conditions', 'allergies',
]);

const REDACTED = '[REDACTED]';

/**
 * Returns a redacted copy of an AuditLogEntry safe for auditor review.
 * Preserves: action, timestamp, resource, result, severity/category if present.
 * Redacts: actor identity, IP address, user agent, sensitive detail fields.
 */
export function redactAuditEntry(entry: AuditLogEntry): AuditLogEntry {
  const redactedDetails: Record<string, string | number | boolean> = {};

  for (const [key, value] of Object.entries(entry.details)) {
    if (SENSITIVE_DETAIL_KEYS.has(key)) {
      redactedDetails[key] = REDACTED;
    } else if (typeof value === 'string' && looksLikePII(value)) {
      redactedDetails[key] = REDACTED;
    } else {
      redactedDetails[key] = value;
    }
  }

  return {
    ...entry,
    actor: redactActor(entry.actor),
    ipAddress: null,
    userAgent: null,
    details: redactedDetails,
  };
}

/**
 * Redact actor field: preserve system actors (system:*) and role hints,
 * but mask session IDs and user IDs.
 */
function redactActor(actor: string): string {
  if (actor.startsWith('system:')) return actor;
  if (actor.startsWith('admin:')) return 'admin:[REDACTED]';
  return '[REDACTED_ACTOR]';
}

/**
 * Heuristic check for PII patterns in string values.
 * Catches emails, phone numbers, SSNs, and IP addresses.
 */
function looksLikePII(value: string): boolean {
  // Email
  if (/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(value)) return true;
  // US phone (10+ digits with optional separators)
  if (/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/.test(value)) return true;
  // SSN pattern
  if (/\b\d{3}-\d{2}-\d{4}\b/.test(value)) return true;
  // IPv4
  if (/\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/.test(value)) return true;
  return false;
}

/** Redact an array of entries */
export function redactAuditEntries(entries: AuditLogEntry[]): AuditLogEntry[] {
  return entries.map(redactAuditEntry);
}
